import React from 'react'
import { Link } from 'gatsby'
import styled from 'styled-components'
import { footerItems } from './index'
import { spacing } from 'src/constants/marketing-ds/spacing'
import { body, heading, typographyMixin } from 'src/constants/marketing-ds/typography'
import { colors } from 'src/constants/marketing-ds/colors'
import { useSelector } from 'react-redux'
import { selectIsTranscendInitialized } from 'src/modules/reducers/transcend'
import { useChatbot } from 'src/modules/hooks/useChatbot'
import { FacebookIcon } from 'src/components/template/compare/components/icons/FacebookIcon'
import { TwitterIcon } from 'src/components/template/compare/components/icons/TwitterIcon'
import { InstagramIcon } from 'src/components/template/compare/components/icons/InstagramIcon'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { SectionContainer } from '../SharedComponents'
import { maxWidth } from 'src/constants/marketing-ds'

const socialLinks = [
  {
    label: 'Instagram',
    url: 'https://www.instagram.com/fuboTV/',
    icon: <InstagramIcon width={18} height={18} />,
  },
  {
    label: 'Facebook',
    url: 'https://www.facebook.com/fuboTV?fref=ts',
    icon: <FacebookIcon width={18} height={18} />,
  },
  {
    label: 'Twitter',
    url: 'https://twitter.com/fuboTV',
    icon: <TwitterIcon width={18} height={18} />,
  },
]

export const footerDescription = {
  en: 'With a global mission to aggregate the best in TV, including premium sports, news and entertainment content, through a single app, Fubo aims to transcend the industry’s current TV model. Ranked among The Americas’ Fastest-Growing Companies 2025 by the Financial Times, the company operates Fubo in the U.S., Canada and Spain and Molotov in France.',
  es: 'La misión de Fubo es transformar el modelo de TV tradicional, reuniendo lo mejor de la televisión: deportes premium, noticias y entretenimiento, en una sola app. La empresa está presente en Estados Unidos, Canadá y España, y en Francia bajo el nombre de Molotov. Recientemente ha sido reconocida por el Financial Times como una de las compañías de más rápido crecimiento en América en 2025.',
}

const FooterSeo = ({ lang = 'en', market = 'us', miceText, isComparePage }) => {
  // Transcend
  const isTranscendInitialized = useSelector(selectIsTranscendInitialized)
  const [showTranscendLink, setShowTranscendLink] = React.useState(false)
  const { isChatbotLoading, handleStartChat, isChatbotEnabled } = useChatbot()
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const showTranscendHandler = (e) => {
    e.preventDefault()
    window.transcend.showConsentManager({
      viewState: 'DoNotSellExplainer',
    })
  }

  const hideTranscendHandler = React.useCallback(
    (e) => {
      if (showTranscendLink && e.key === 'Escape') {
        window.transcend.hideConsentManager()
      }
    },
    [showTranscendLink]
  )

  React.useEffect(() => {
    document.addEventListener('keydown', hideTranscendHandler)
    return () => document.removeEventListener('keydown', hideTranscendHandler)
  }, [hideTranscendHandler])

  React.useEffect(() => {
    if (isTranscendInitialized) {
      setShowTranscendLink(
        window.transcend?.ready &&
          window.airgap?.getRegimes &&
          window.airgap.getRegimes().has('DoNotSell')
      )
    }
  }, [isTranscendInitialized])

  // Solvvy

  const solvyLoadingText = lang === 'es' ? 'Cargando...' : 'Loading...'
  const solvyButtonText = lang === 'es' ? 'Contáctanos' : 'Contact Us'

  const currentYear = new Date().getFullYear()
  const bottomLinks = [
    {
      label: { en: 'Your Privacy Choices', es: 'Tus Opciones de Privacidad' },
      onClick: showTranscendHandler,
    },
    {
      label: { en: 'Unsolicited Submissions', es: 'Envíos No Solicitados' },
      url: 'https://legal.fubo.tv/policies/en-US/?name=unsolicited-submissions-policy',
    },
    {
      label: { en: 'Terms', es: 'Términos del servicio' },
      url: 'https://www.fubo.tv/documents/USA/terms-of-service',
    },
    {
      label: { en: 'Privacy', es: 'Privacidad' },
      url: 'https://www.fubo.tv/documents/USA/privacy-policy',
    },
    {
      label: { en: 'Cookie Policy', es: 'Política de Cookies' },
      url: 'https://www.fubo.tv/documents/USA/cookie-policy',
    },
  ]

  const onLinkClick = (label) => {
    fireUIInteractionEvent(
      {
        element: label,
        component: 'footer_link_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <SectionContainer backgroundColor={isComparePage ? '#000f26' : colors.slate[30]}>
      <FooterContainer isComparePage={isComparePage}>
        <FooterContent>
          {market !== 'canada' && (
            <>
              {/* Top Section - Link Groups */}
              <TopSection>
                {footerItems.map((section, index) => (
                  <LinkSection key={index}>
                    <SectionHeader>{section.header[lang]}</SectionHeader>
                    <LinksContainer>
                      {section.items.map((item, itemIndex) => (
                        <StyledLink
                          key={itemIndex}
                          to={item.url}
                          onClick={() => onLinkClick(item.label)}
                        >
                          <span>{item.label}</span>
                        </StyledLink>
                      ))}
                    </LinksContainer>
                  </LinkSection>
                ))}
              </TopSection>

              {/* Divider */}
              <Divider />
            </>
          )}

          {/* Bottom Section */}
          <BottomSection>
            {/* Content Row - Company Description and Social/Contact */}
            <ContentRow>
              <CompanyDescription>
                {miceText && <span>{miceText}</span>}
                {footerDescription[lang]}
              </CompanyDescription>

              <RightContent>
                {/* Social Media Section */}
                <SocialSection>
                  <SocialLabel>{lang === 'es' ? 'Encuéntranos en:' : 'Follow on:'}</SocialLabel>
                  <SocialIcons>
                    {socialLinks.map(({ url, icon, label }, index) => (
                      <SocialIcon key={index}>
                        <a href={url} target="_blank" rel="noopener noreferrer" aria-label={label}>
                          {icon}
                        </a>
                      </SocialIcon>
                    ))}
                  </SocialIcons>
                </SocialSection>

                {/* Contact Button */}
                {isChatbotEnabled && (
                  <ContactButton onClick={handleStartChat}>
                    {' '}
                    {isChatbotLoading ? solvyLoadingText : solvyButtonText}
                  </ContactButton>
                )}
              </RightContent>
            </ContentRow>

            {/* Bottom Row - Copyright and Links */}
            <BottomRow>
              <Copyright>@ {currentYear} Fubo</Copyright>

              <BottomLinks>
                {bottomLinks.map(({ label, url, onClick }, index) =>
                  onClick ? (
                    <BottomButton key={index} onClick={onClick}>
                      <span>{label[lang]}</span>
                    </BottomButton>
                  ) : (
                    <BottomLink key={index} href={url} target="_blank" rel="noopener noreferrer">
                      <span>{label[lang]}</span>
                    </BottomLink>
                  )
                )}
              </BottomLinks>
            </BottomRow>
          </BottomSection>
        </FooterContent>
      </FooterContainer>
    </SectionContainer>
  )
}

export default FooterSeo

// Styled Components
const FooterContainer = styled.footer`
  color: #ffffff;
  ${typographyMixin(body.sm)}
  width: 100%;
  box-sizing: border-box;
`

const FooterContent = styled.div`
  max-width: ${maxWidth.desktop};
  margin: 0 auto;
  padding: ${spacing.xxxl} 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxl};

  @media (max-width: 768px) {
    padding: ${spacing.xl} 0;
  }

  @media (max-width: 480px) {
    padding: ${spacing.xl} 0;
    gap: ${spacing.lg};
  }
`

const TopSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${spacing.xxxl};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${spacing.xl};
  }
`

const LinkSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`

const SectionHeader = styled.h3`
  ${typographyMixin(heading.sm)}
  margin: 0;
  text-shadow:
    0px 4px 4px 0px rgba(0, 0, 0, 0.25),
    0px 4px 4px 0px rgba(0, 0, 0, 0.25);
`

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`

const StyledLink = styled(Link)`
  ${typographyMixin(body.sm)}
  color: #ffffff;
  text-decoration: none;
  text-shadow:
    0px 4px 4px 0px rgba(0, 0, 0, 0.25),
    0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  transition: opacity 0.2s ease;
  word-break: break-word;
  display: inline;
  width: fit-content;

  span {
    text-decoration: underline;
    text-decoration-color: rgba(255, 255, 255, 0.8);
    text-decoration-thickness: 1px;
    text-underline-offset: 3.5px;
  }

  &:hover {
    color: ${colors.slate[100]};
    transition: color 0.15s ease-in-out;

    span {
      text-decoration-color: ${colors.slate[100]};
    }
  }
`

const Divider = styled.div`
  height: 1px;
  background-color: #2c2f3d;
  width: 100%;
`

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xl};
  width: 100%;

  @media (max-width: 768px) {
    gap: ${spacing.lg};
  }
`

const ContentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${spacing.xxl};
  align-items: start;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: ${spacing.md};
  }
`

const CompanyDescription = styled.p`
  ${typographyMixin(body.sm)}
  color: #ffffff;
  margin: 0;
  line-height: 1.7;
  max-width: 600px;

  span {
    margin-bottom: ${spacing.sm};
    display: block;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    ${typographyMixin(body.xs)}
  }
`

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${spacing.xl};
  min-width: 0;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: ${spacing.lg};
  }
`

const SocialSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};

  @media (max-width: 768px) {
  }
`

const SocialLabel = styled.span`
  ${typographyMixin(heading.sm)}
  color: #ffffff;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 768px) {
    text-align: left;
  }
`

const SocialIcons = styled.div`
  display: flex;
  gap: ${spacing.md};
  flex-shrink: 0;
`

const SocialIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 2px;
  flex-shrink: 0;
  &:hover {
    svg path {
      fill: ${colors.slate[100]};
    }
  }
`

const ContactButton = styled.button`
  ${typographyMixin(heading.sm)}
  color: #ffffff;
  background-color: transparent;
  border: 2px solid #ffffff;
  border-radius: 4px;
  padding: ${spacing.sm} ${spacing.xxl};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
`

const BottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.xl};

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: ${spacing.md};
  }

  @media (max-width: 480px) {
    gap: ${spacing.lg};
  }
`

const Copyright = styled.p`
  ${typographyMixin(body.sm)}
  color: #ffffff;
  margin: 0;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`

const BottomLinks = styled.div`
  display: flex;
  gap: ${spacing.sm};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: ${spacing.xs};
    justify-content: flex-start;
  }

  @media (max-width: 480px) {
    gap: ${spacing.xs};
  }
`

const BottomLink = styled.a`
  ${typographyMixin(body.sm)}
  color: #ffffff;
  text-decoration: none;

  transition: opacity 0.2s ease;
  white-space: nowrap;
  display: inline;
  width: fit-content;
  cursor: pointer;

  span {
    text-decoration: underline rgba(255, 255, 255, 0.8) 1px;
    text-underline-offset: 3.5px;
  }

  &:hover {
    color: ${colors.slate[100]};
    span {
      text-decoration-color: ${colors.slate[100]};
    }
  }

  @media (max-width: 480px) {
    ${typographyMixin(body.xs)}
  }
`

const BottomButton = styled.button`
  ${typographyMixin(body.sm)}
  color: #ffffff;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  transition: opacity 0.2s ease;
  white-space: nowrap;
  display: inline;
  width: fit-content;
  cursor: pointer;

  span {
    text-decoration: underline rgba(255, 255, 255, 0.8) 1px;
    text-underline-offset: 2px;
  }

  &:hover {
    color: ${colors.slate[100]};
    span {
      text-decoration-color: ${colors.slate[100]};
    }
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`
